import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProfileDropdown from './ProfileDropdown';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartItemsCount, openCart } = useCart();
  const { user, logout, isAdmin } = useAuth();

  useEffect(() => {
    // Only close mobile menu on navigation
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/wellness-accessories', label: 'Wellness Accessories' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact Us' }
  ];
  const resolvedNavItems = isAdmin
    ? [...navItems, { path: '/admin', label: 'Admin' }]
    : navItems;

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/75 backdrop-blur-xl shadow-[0_8px_22px_rgba(23,51,50,0.10)] border-b border-compassionScale-100/70"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 lg:gap-3 group whitespace-nowrap shrink-0"
            data-testid="logo-link"
          >
            <div className="relative shrink-0">
              <span className="absolute -inset-3 rounded-full bg-gradient-to-br from-compassion via-compassionScale-300 to-sacredScale-300 opacity-55 blur-md" />
              <img
                src={`${process.env.PUBLIC_URL}/mystic-prana-logo.jpg`}
                alt="Mystic Prana Logo"
                className="relative h-[4.5rem] w-[4.5rem] object-cover shadow-lg ring-1 ring-white/30"
                onError={(event) => {
                  event.currentTarget.src = `${process.env.PUBLIC_URL}/mystic-prana-logo.svg`;
                }}
              />
            </div>
            <div className="flex h-[4.5rem] flex-col justify-center min-w-0">
              <span className="font-heading font-semibold text-lg lg:text-xl whitespace-nowrap transition-colors duration-300 text-spiritual leading-tight">
                Mystic Prana
              </span>
              <span className="text-[10px] lg:text-xs font-light tracking-[0.16em] lg:tracking-[0.2em] uppercase whitespace-nowrap transition-colors duration-300 text-compassion-foreground leading-tight">
                Loving Sanctuary
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-center flex-1 space-x-4 lg:space-x-8 ml-8 lg:ml-10 mr-4">
            {resolvedNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-105 ${
                  location.pathname === item.path
                    ? 'text-spiritual'
                    : 'text-spiritual hover:text-spiritual/90'
                } group`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-compassionScale-400 via-spiritual to-sacredScale-400 transition-all duration-300 group-hover:w-full ${
                  location.pathname === item.path ? 'w-full' : ''
                }`} />
              </Link>
            ))}
            
            {/* Cart Button - Only visible when logged in */}
            {user && (
              <button
                onClick={() => openCart()}
                className="relative px-2 lg:px-3 py-2 rounded-lg text-xs lg:text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-105 text-spiritual hover:text-spiritual/90"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6" />
                {getCartItemsCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-br from-spiritual to-compassion text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartItemsCount()}
                  </span>
                )}
              </button>
            )}

            {/* Profile or Auth Links - Show profile when logged in, otherwise show login/register */}
            {user ? (
              <ProfileDropdown />
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-105 text-spiritual hover:text-spiritual/90"
                  data-testid="login-btn"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-4 lg:px-6 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-105 hover:shadow-lg bg-spiritual text-white hover:bg-spiritual/90"
                  data-testid="register-btn"
                >
                  Register
                </Link>
              </>
            )}
            
            <Link
              to="/contact"
              className="px-4 lg:px-6 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-105 hover:shadow-lg bg-gradient-to-r from-spiritual via-compassion to-sacred text-white hover:from-spiritual/90 hover:via-compassion/90 hover:to-sacred/90"
              data-testid="book-session-btn"
            >
              Begin Journey
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors duration-300 text-spiritual hover:bg-compassionScale-50"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl rounded-b-2xl mt-2 py-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300 border border-compassionScale-100/70">
            {resolvedNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 hover:bg-compassionScale-50 ${
                  location.pathname === item.path
                    ? 'text-spiritual bg-compassionScale-50 border-l-4 border-sacred pl-5'
                    : 'text-spiritual'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Cart Button - Only visible when logged in */}
            {user && (
              <button
                onClick={() => openCart()}
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium whitespace-nowrap text-spiritual hover:bg-compassionScale-50 transition-all duration-300"
                data-testid="mobile-cart-button"
              >
                <span className="flex items-center">
                  <ShoppingBag className="w-4 h-4 mr-3" />
                  Cart
                </span>
                {getCartItemsCount() > 0 && (
                  <span className="bg-gradient-to-br from-spiritual to-compassion text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartItemsCount()}
                  </span>
                )}
              </button>
            )}

            {/* Mobile Auth */}
            {user ? (
              <div className="px-4 py-2 border-t border-gray-200">
                <ProfileDropdown />
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block px-4 py-2 text-sm font-medium whitespace-nowrap text-spiritual hover:bg-compassionScale-50 transition-all duration-300"
                  data-testid="mobile-login-btn"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block mx-4 my-2 px-4 py-2 text-center text-sm font-medium whitespace-nowrap text-white bg-spiritual hover:bg-spiritual/90 rounded-lg transition-all duration-300"
                  data-testid="mobile-register-btn"
                >
                  Register
                </Link>
              </>
            )}
            
            <div className="px-4 py-2 border-t border-gray-200">
              <Link
                to="/contact"
                className="block w-full text-center px-4 py-2 text-sm font-medium whitespace-nowrap bg-gradient-to-r from-spiritual via-compassion to-sacred text-white rounded-lg hover:from-spiritual/90 hover:via-compassion/90 hover:to-sacred/90 transition-colors duration-300"
                data-testid="mobile-book-session-btn"
              >
                Begin Journey
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
